import { useCallback } from 'react'
import { useNavigate } from 'react-router'
import { useFeature } from '../../providers/featureFlags'
import { useAppSelector } from '../../hooks/reduxToolkit'
import { Tooltip } from '../tooltip'
import { ProviderModel } from '../../reduxToolkit/providersSlice'
import type { ConversationWithMessagesAndPhoneCalls } from '../../reduxToolkit/conversationsSelectors'
import { present } from '../../../lib/util/present'
import { selectTodosForExpense } from '../../reduxToolkit/selectors/todos'
import { isOpenTodo } from '../../reduxToolkit/todosSlice'

interface AIPhoneCallButtonProps {
  expenseId: string

  provider: ProviderModel
  isWhitelisted: boolean
  existingConversation?: ConversationWithMessagesAndPhoneCalls
}

type DisabledReason =
  | 'no_phone'
  | 'not_authorized'
  | 'not_whitelisted'
  | 'no_open_todo'
  | null

function getDisabledReason(
  providerPhone: string | null | undefined,
  isAuthorized: boolean,
  isWhitelisted: boolean,
  hasOpenItemizedBillTodo: boolean
): DisabledReason {
  // Check: Provider has a phone number
  if (!providerPhone) {
    return 'no_phone'
  }

  // Check: User has authorized communication with the provider
  if (!isAuthorized) {
    return 'not_authorized'
  }

  // Check: Phone number is whitelisted for calling
  if (!isWhitelisted) {
    return 'not_whitelisted'
  }

  // Check: There is an open expense needing an itemized bill
  if (!hasOpenItemizedBillTodo) {
    return 'no_open_todo'
  }

  return null
}

function getDisabledTooltip(reason: DisabledReason): string {
  switch (reason) {
    case 'no_phone':
      return 'Provider has no phone number'
    case 'not_authorized':
      return 'You must authorize communication with this provider first'
    case 'no_open_todo':
      return 'No open itemized bill request for this expense'
    case 'not_whitelisted':
      return 'The provider must be whitelisted for AI calling.  Contact your plan administrator.'
    default:
      return ''
  }
}

export function AIPhoneCallButton({
  expenseId,
  provider,
  isWhitelisted,
  existingConversation,
}: AIPhoneCallButtonProps) {
  const isAiPhoneCallingEnabled = useFeature('ai_phone_calling')
  const navigate = useNavigate()

  const providerPhone = provider.billing_phone;
  const isAuthorized = present(provider.communication_authorized_at);
  const hasOpenItemizedBillTodo = useAppSelector((s) => !!selectTodosForExpense(expenseId)(s).filter(isOpenTodo).find((todo) => todo.todo_type == 'itemized-bill') )

  const disabledReason = getDisabledReason(
    providerPhone,
    isAuthorized,
    isWhitelisted,
    hasOpenItemizedBillTodo
  )
  const isDisabled = disabledReason !== null
  const tooltipText = getDisabledTooltip(disabledReason)
  const hasPreviousCall = (existingConversation?.phone_calls && existingConversation.phone_calls.length > 0)

  const handleNavigateToCallScreen = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    navigate(`/providers/${provider.id}/call?expenseId=${expenseId}`)
  }, [navigate, provider.id, expenseId])

  // Hide button entirely if feature is not enabled
  if (!isAiPhoneCallingEnabled) {
    return null
  }

  const buttonContent = (
    <button
      className="btn btn-outline-primary btn-sm"
      onClick={handleNavigateToCallScreen}
      disabled={isDisabled}
    >
      <i className="material-icons" style={{ fontSize: '16px', verticalAlign: 'middle' }}>phone</i>
      &nbsp;{hasPreviousCall ? 'Call Again' : 'AI Call'}
    </button>
  )

  if (isDisabled && tooltipText) {
    return (
      <Tooltip tooltip={tooltipText} placement="top">
        {buttonContent}
      </Tooltip>
    )
  }

  return buttonContent
}
